/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Field, ErrorMessage, FieldArray } from 'formik';
import {
   Grid,
   TextField,
   Box,
   Typography,
   Button,
   Select,
   MenuItem,
   FormControl,
   InputLabel,
} from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../../../Utils/RichEditor';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { useGetCurrenciesQuery } from '../../../Redux/Service/CurrencyApi';

function StepTwoUpdate({ isSubmitting, values, setFieldValue, touched, errors, initialValues }) {
   // ─── StepFourUpdate state ─────────────────────────────────────────────────
   const [selectedCurrency, setSelectedCurrency] = useState(null);
   const { data: currencies, isLoading, error } = useGetCurrenciesQuery();

   // ─── Shared Redux prefilled data ──────────────────────────────────────────
   const contractPrefilledData = useSelector(
      (state) => state.contractState.contractUpdatePrefilledData,
   );

   // ─── StepTwoUpdate: currency for labels ───────────────────────────────────
   const contractCurrencyFromRedux = useSelector(
      (state) => state.contractState?.contractCurrency,
   );

   const currencyToShow = (() => {
      const list = currencies?.response || [];
      if (values?.currency_id) {
         const found = list.find((c) => c.id === parseInt(values.currency_id));
         if (found) return found;
      }
      if (contractPrefilledData?.currency?.id) {
         const found = list.find((c) => c.id === contractPrefilledData.currency.id);
         if (found) return found;
      }
      if (contractCurrencyFromRedux) return contractCurrencyFromRedux;
      if (list.length) return list[0];
      return null;
   })();

   // ─── StepFourUpdate: sync selectedCurrency with Formik value ─────────────
   useEffect(() => {
      if (currencies?.response && currencies.response.length > 0) {
         if (values.currency_id) {
            const selected = currencies.response.find(
               (currency) => currency.id === parseInt(values.currency_id),
            );
            if (selected) {
               setSelectedCurrency(selected);
            }
         } else if (contractPrefilledData?.currency?.id) {
            const matchedCurrency = currencies.response.find(
               (currency) => currency.id === contractPrefilledData.currency.id,
            );
            if (matchedCurrency) {
               setSelectedCurrency(matchedCurrency);
               setFieldValue('currency_id', matchedCurrency.id);
            } else {
               setSelectedCurrency(currencies.response[0]);
               setFieldValue('currency_id', currencies.response[0].id);
            }
         } else {
            setSelectedCurrency(currencies.response[0]);
            setFieldValue('currency_id', currencies.response[0].id);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currencies, values.currency_id, contractPrefilledData, setFieldValue]);

   // ─── StepFourUpdate: currency change handler ──────────────────────────────
   const handleCurrencyChange = (event) => {
      const selected = currencies.response.find(
         (currency) => currency.id === parseInt(event.target.value),
      );
      setSelectedCurrency(selected);
      setFieldValue('currency_id', selected.id);
      setFieldValue('currencyId', selected.id);
   };

   // ─── StepFourUpdate: tax calculations ────────────────────────────────────
   const calculatedTaxAmount =
      (values.total_development_cost * (values.payable_tax_percentage || 0)) / 100;
   const totalAmountIncludingTax =
      Number(values.total_development_cost) + Number(calculatedTaxAmount);

   // ─── StepTwoUpdate: remove item helper ───────────────────────────────────
   const handleRemoveItem = (id, arrayHelpers) => {
      arrayHelpers.remove(id);
   };

   const totalMilestoneCost = values.deliverable_milestones.reduce(
      (sum, milestone) => sum + (Number(milestone.cost) || 0),
      0,
   )
   const commencementValue = Number(values.commencementValue) || 0
   const totalWithCommencement = totalMilestoneCost + commencementValue

   // ─── StepTwoUpdate: dm_structure prefill ─────────────────────────────────
   useEffect(() => {
      if (values.__stepTwoPrefilled) return;

      if (
         contractPrefilledData &&
         Array.isArray(contractPrefilledData.dm) &&
         contractPrefilledData.dm.length > 0 &&
         (!values.dm_structure ||
            values.dm_structure.length === 0 ||
            (values.dm_structure.length === 1 &&
               (!values.dm_structure[0].milname || values.dm_structure[0].milname === '') &&
               values.dm_structure[0].delivery === 0))
      ) {
         setFieldValue('dm_structure', contractPrefilledData.dm);
      }
   }, [contractPrefilledData, setFieldValue, values.dm_structure, values.__stepTwoPrefilled]);

   // ─── StepTwoUpdate: full prefill on mount ────────────────────────────────
   useEffect(() => {
      if (!contractPrefilledData) return;
      if (values.__stepTwoPrefilled) return;

      if (
         (!values.note || values.note === '') &&
         contractPrefilledData.note !== undefined &&
         contractPrefilledData.note !== null &&
         contractPrefilledData.note !== ''
      ) {
         setFieldValue('note', contractPrefilledData.note);
      }

      if (
         values?.deliverable_milestones &&
         values.deliverable_milestones.length > 0 &&
         values.deliverable_milestones[0].cost === 0 &&
         (!values.deliverable_milestones[0].milestone ||
            values.deliverable_milestones[0].milestone === '') &&
         contractPrefilledData?.milestone &&
         contractPrefilledData.milestone.length > 0
      ) {
         setFieldValue('deliverable_milestones', contractPrefilledData.milestone);
      }

      if (
         (!values.commencementLabel || values.commencementLabel === '') &&
         contractPrefilledData.commencementLabel
      ) {
         setFieldValue('commencementLabel', contractPrefilledData.commencementLabel);
      }

      if (
         (!values.commencementValue || values.commencementValue === '') &&
         contractPrefilledData.commencementValue
      ) {
         setFieldValue('commencementValue', contractPrefilledData.commencementValue);
      }

      setFieldValue('__stepTwoPrefilled', true, false);
      // eslint-disable-next-line
   }, [contractPrefilledData, setFieldValue]);

   return (
      <div className="card stepCard">
         <Box className="card-body" sx={{ padding: '15px' }}>
            <Grid container spacing={2}>

               {/* ── COMMERCIAL DETAILS (from StepFourUpdate) ───────────────── */}
               <Grid item md={12} xs={12}>
                  <Box className="commonBox">
                     <Grid container spacing={3}>
                        <Grid item xs={12}>
                           <Typography variant="h6" gutterBottom align="left">
                              Commercial Details
                           </Typography>
                           <Grid container spacing={2} sx={{ my: 1 }}>
                              <Grid item xs={12}>
                                 <Grid container spacing={2}>

                                    {/* Currency Select */}
                                    <Grid item md={6} xs={12}>
                                       <label htmlFor="currency">
                                          Select Currency <span style={{ color: 'red' }}>*</span>
                                       </label>
                                       {isLoading ? (
                                          <p>Loading currencies...</p>
                                       ) : error ? (
                                          <p>Error fetching currencies</p>
                                       ) : (
                                          <Field
                                             name="currency_id"
                                             as={Select}
                                             fullWidth
                                             variant="outlined"
                                             id="currency"
                                             value={values.currency_id || ''}
                                             onChange={(e) => {
                                                handleCurrencyChange(e);
                                                setFieldValue('currency_id', e.target.value);
                                             }}
                                             error={
                                                touched.currency_id && Boolean(errors.currency_id)
                                             }
                                          >
                                             {currencies?.response?.map((currency) => (
                                                <MenuItem key={currency.id} value={currency.id}>
                                                   {currency.name} ({currency.symbol})
                                                </MenuItem>
                                             ))}
                                          </Field>
                                       )}
                                    </Grid>

                                    {/* Total Development Cost */}
                                    <Grid item md={6} xs={12}>
                                       <label htmlFor="total_development_cost">
                                          Total Development Cost{' '}
                                          <span style={{ color: 'red' }}>*</span>
                                       </label>
                                       <Field
                                          fullWidth
                                          label={
                                             selectedCurrency
                                                ? `Total Development Cost (in ${selectedCurrency?.symbol || selectedCurrency?.code}) *`
                                                : `Total Development Cost *`
                                          }
                                          name="total_development_cost"
                                          variant="outlined"
                                          type="number"
                                          value={values.total_development_cost}
                                          onChange={(e) => {
                                             setFieldValue('total_development_cost', e.target.value);
                                          }}
                                          onWheel={(e) => e.target.blur()}
                                          onFocus={(e) => e.target.select()}
                                          as={TextField}
                                          error={
                                             touched.total_development_cost &&
                                             Boolean(errors.total_development_cost)
                                          }
                                          helperText={
                                             touched.total_development_cost &&
                                             errors.total_development_cost
                                          }
                                       />
                                    </Grid>

                                    {/* Payable Tax Percentage */}
                                    <Grid item md={6} xs={12}>
                                       <label htmlFor="payable_tax_percentage">
                                          Payable Tax Percentage (%)
                                       </label>
                                       <Field
                                          fullWidth
                                          label="Payable Tax Percentage (%)"
                                          name="payable_tax_percentage"
                                          variant="outlined"
                                          type="number"
                                          value={values.payable_tax_percentage}
                                          onChange={(e) => {
                                             const value = Math.min(e.target.value, 100);
                                             setFieldValue('payable_tax_percentage', value);
                                          }}
                                          onWheel={(e) => e.target.blur()}
                                          onFocus={(e) => e.target.select()}
                                          as={TextField}
                                          error={
                                             touched.payable_tax_percentage &&
                                             Boolean(errors.payable_tax_percentage)
                                          }
                                          helperText={
                                             touched.payable_tax_percentage &&
                                             errors.payable_tax_percentage
                                          }
                                       />
                                    </Grid>

                                    {/* Tax Amount */}
                                    <Grid item md={6} xs={12}>
                                       <label htmlFor="taxAmount">Tax Amount</label>
                                       <Typography
                                          variant="body1"
                                          style={{
                                             width: '100%',
                                             padding: '8px',
                                             border: '1px solid #e0e0e0',
                                             borderRadius: '4px',
                                             backgroundColor: '#f5f5f5',
                                          }}
                                       >
                                          {selectedCurrency
                                             ? `${selectedCurrency?.symbol || selectedCurrency?.code} ${calculatedTaxAmount.toFixed(2)}`
                                             : calculatedTaxAmount.toFixed(2)}
                                       </Typography>
                                    </Grid>

                                    {/* Total Amount Including Tax */}
                                    <Grid item md={6} xs={12}>
                                       <label htmlFor="totalAmount">
                                          Total Amount Including Tax
                                       </label>
                                       <Typography
                                          variant="body1"
                                          style={{
                                             width: '100%',
                                             padding: '8px',
                                             border: '1px solid #e0e0e0',
                                             borderRadius: '4px',
                                             backgroundColor: '#f5f5f5',
                                          }}
                                       >
                                          {selectedCurrency
                                             ? `${selectedCurrency?.symbol || selectedCurrency?.code} ${totalAmountIncludingTax.toFixed(2)}`
                                             : totalAmountIncludingTax.toFixed(2)}
                                       </Typography>
                                    </Grid>

                                 </Grid>
                              </Grid>
                           </Grid>
                        </Grid>
                     </Grid>
                  </Box>
               </Grid>

               {/* ── PROJECT DEVELOPMENT TIME (from StepTwoUpdate) ──────────── */}
               <Grid item xs={12}>
                  <Box className="commonBox">
                     <Typography variant="h6" gutterBottom align="left">
                        Project Development Time <span style={{ color: 'red' }}>*</span>
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                           (e.g. 6 months for development)
                        </Typography>
                     </Typography>
                     <Field
                        fullWidth
                        label={
                           <>
                              Project Development Time <span style={{ color: 'red' }}>*</span>
                           </>
                        }
                        name="project_development"
                        variant="outlined"
                        as={TextField}
                        value={values.project_development}
                        placeholder="Enter the Project development time"
                        onChange={(e) => setFieldValue('project_development', e.target.value)}
                        error={touched.project_development && Boolean(errors.project_development)}
                        helperText={touched.project_development &&
                           errors.project_development ? errors.project_development : 'e.g 6  months development time'
                        }
                     />
                  </Box>
               </Grid>

               {/* ── DELIVERABLE MILESTONES / DM STRUCTURE (from StepTwoUpdate) ── */}
               <Grid item xs={12}>
                  <Box className="commonBox">
                     <Typography variant="h6" gutterBottom align="left">
                        Deliverable Milestones
                     </Typography>
                     <FieldArray name="dm_structure">
                        {(arrayHelpers) => (
                           <div>
                              {!values?.dm_structure || values.dm_structure.length === 0 ? (
                                 <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                       onClick={() =>
                                          arrayHelpers.push({
                                             id: 1,
                                             milname: '',
                                             delivery: 0,
                                             type: 'Days',
                                          })
                                       }
                                       startIcon={<AddIcon />}
                                       variant="contained"
                                       className="addFieldBtn"
                                    >
                                       Add Milestone
                                    </Button>
                                 </Box>
                              ) : (
                                 values?.dm_structure?.map((val, index) => (
                                    <Grid
                                       container
                                       spacing={2}
                                       key={val.id || index}
                                       sx={{ my: 1 }}
                                    >
                                       <Grid item xs={12}>
                                          <Grid container spacing={2}>
                                             <Grid item xs={5}>
                                                <Field
                                                   fullWidth
                                                   label="Milestone"
                                                   name={`dm_structure.${index}.milname`}
                                                   value={val.milname}
                                                   placeholder="Enter the Milestone name"
                                                   variant="outlined"
                                                   as={TextField}
                                                   error={
                                                      touched.dm_structure?.[index]?.milname &&
                                                      Boolean(errors.dm_structure?.[index])
                                                   }
                                                   helperText={
                                                      touched.dm_structure?.[index]?.milname &&
                                                         typeof errors.dm_structure?.[index] === 'string'
                                                         ? errors.dm_structure[index]
                                                         : ''
                                                   }
                                                />
                                             </Grid>

                                             <Grid item xs={3}>
                                                <Field
                                                   fullWidth
                                                   label="Delivery"
                                                   name={`dm_structure.${index}.delivery`}
                                                   variant="outlined"
                                                   type="number"
                                                   inputProps={{ step: '1', min: 0 }}
                                                   value={val.delivery}
                                                   onWheel={(e) => e.target.blur()}
                                                   onFocus={(e) => e.target.select()}
                                                   as={TextField}
                                                   error={
                                                      touched.dm_structure?.[index]?.delivery &&
                                                      Boolean(errors.dm_structure?.[index])
                                                   }
                                                   helperText={
                                                      touched.dm_structure?.[index]?.delivery &&
                                                         typeof errors.dm_structure?.[index] === 'string'
                                                         ? errors.dm_structure[index]
                                                         : ''
                                                   }
                                                />
                                                <Typography
                                                   variant="caption"
                                                   color="GrayText"
                                                   sx={{ mt: 0.5, display: 'block' }}
                                                >
                                                   (e.g. 10)
                                                </Typography>
                                             </Grid>

                                             <Grid item xs={2}>
                                                <Field
                                                   fullWidth
                                                   label="Type"
                                                   name={`dm_structure.${index}.type`}
                                                   as={Select}
                                                   variant="outlined"
                                                   value={val.type}
                                                   onChange={(e) =>
                                                      setFieldValue(
                                                         `dm_structure.${index}.type`,
                                                         e.target.value,
                                                      )
                                                   }
                                                >
                                                   <MenuItem value="Days">Days</MenuItem>
                                                   <MenuItem value="Month">Month</MenuItem>
                                                   <MenuItem value="Weeks">Weeks</MenuItem>
                                                </Field>
                                                {touched.dm_structure?.[index]?.type &&
                                                   errors.dm_structure?.[index]?.type && (
                                                      <Typography variant="caption" color="error">
                                                         {errors.dm_structure?.[index]?.type}
                                                      </Typography>
                                                   )}
                                             </Grid>

                                             <Grid item xs={2} align="right">
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      gap: 1,
                                                      justifyContent: 'flex-end',
                                                   }}
                                                >
                                                   {index === 0 && (
                                                      <Button
                                                         onClick={() =>
                                                            arrayHelpers.push({
                                                               id:
                                                                  (values?.dm_structure?.length ||
                                                                     0) + 1,
                                                               milname: '',
                                                               delivery: 0,
                                                               type: 'Days',
                                                            })
                                                         }
                                                         startIcon={<AddIcon />}
                                                         variant="contained"
                                                         className="addFieldBtn"
                                                      />
                                                   )}
                                                   <Button
                                                      onClick={() =>
                                                         handleRemoveItem(index, arrayHelpers)
                                                      }
                                                      startIcon={<DeleteIcon />}
                                                      variant="contained"
                                                      className="deleteFieldBtn"
                                                   />
                                                </Box>
                                             </Grid>
                                          </Grid>
                                       </Grid>
                                    </Grid>
                                 ))
                              )}
                           </div>
                        )}
                     </FieldArray>
                  </Box>
               </Grid>

               {/* ── COMMENCEMENT FEES (from StepTwoUpdate) ─────────────────── */}
               <Grid item xs={12}>
                  <Box className="commonBox">
                     <Typography variant="h6" gutterBottom align="left">
                        Commencement Fees <span style={{ color: 'red' }}>*</span>
                     </Typography>
                     <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={6} md={4}>
                           <Field
                              fullWidth
                              label={
                                 <>
                                    Commencement Label <span style={{ color: 'red' }}>*</span>
                                 </>
                              }
                              name="commencementLabel"
                              as={TextField}
                              placeholder="Enter the commencement label"
                              variant="outlined"
                              size="medium"
                              InputLabelProps={{ shrink: true }}
                              error={
                                 touched.commencementLabel && Boolean(errors.commencementLabel)
                              }
                              helperText={touched.commencementLabel && errors.commencementLabel}
                           />
                           <Typography
                              variant="caption"
                              color="GrayText"
                              sx={{ mt: 0.5, display: 'block' }}
                           >
                              (e.g. Deposit, Security, etc)
                           </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                           <Field
                              fullWidth
                              label={
                                 <>
                                    Commencement Value <span style={{ color: 'red' }}>*</span>
                                 </>
                              }
                              name="commencementValue"
                              placeholder="Enter the commencement value in numbers"
                              type="number"
                              as={TextField}
                              variant="outlined"
                              size="medium"
                              onWheel={(e) => e.target.blur()}
                              onFocus={(e) => e.target.select()}
                              inputProps={{ step: '0.01', min: 0 }}
                              InputLabelProps={{ shrink: true }}
                              error={
                                 touched.commencementValue && Boolean(errors.commencementValue)
                              }
                              helperText={touched.commencementValue && errors.commencementValue}
                           />
                           <Typography
                              variant="caption"
                              color="GrayText"
                              sx={{ mt: 0.5, display: 'block' }}
                           >
                              (e.g. 1000)
                           </Typography>
                        </Grid>
                     </Grid>
                  </Box>
               </Grid>

               {/* ── PAYMENT MILESTONES + NOTE (from StepTwoUpdate) ─────────── */}
               <Grid item xs={12}>
                  <Box className="commonBox">
                     <Typography variant="h6" gutterBottom align="left">
                        Payment Milestones <span style={{ color: 'red' }}>*</span>
                     </Typography>
                     <FieldArray name="deliverable_milestones">
                        {(arrayHelpers) => (
                           <>
                              {values?.deliverable_milestones?.map((val, index) => {
                                 const milestoneTooLong = String(val.milestone || '').length > 50;
                                 let milestoneEmpty

                                 if (touched.deliverable_milestones?.[index]?.milestone) {
                                    milestoneEmpty = !val.milestone || String(val.milestone).trim() === '';
                                 }
                                 let costInvalid
                                 if (touched.deliverable_milestones?.[index]?.cost) {
                                    costInvalid = val.cost === '' ||
                                       val.cost === null ||
                                       val.cost === undefined ||
                                       Number(val.cost) <= 0;
                                 }
                                 return (
                                    <Grid container spacing={2} key={val.id} sx={{ my: 1 }}>
                                       <Grid item xs={12}>
                                          <Grid container spacing={2}>
                                             <Grid item xs={3}>
                                                <Field
                                                   fullWidth
                                                   label={
                                                      <>
                                                         Milestone{' '}
                                                         <span style={{ color: 'red' }}>*</span>
                                                      </>
                                                   }
                                                   name={`deliverable_milestones.${index}.milestone`}
                                                   value={val.milestone}
                                                   variant="outlined"
                                                   as={TextField}
                                                   error={
                                                      milestoneEmpty ||
                                                      milestoneTooLong ||
                                                      (touched.deliverable_milestones?.[index]?.milestone &&
                                                         Boolean(
                                                            errors.deliverable_milestones?.[index]?.milestone,
                                                         ))
                                                   }
                                                   // NEW: shows the message immediately when empty, regardless of touched
                                                   helperText={
                                                      milestoneEmpty
                                                         ? 'Milestone name is required'
                                                         : milestoneTooLong
                                                            ? 'Milestone name should be within 50 characters'
                                                            : (touched.deliverable_milestones?.[index]?.milestone &&
                                                               errors.deliverable_milestones?.[index]?.milestone) ||
                                                            ''
                                                   }
                                                />
                                             </Grid>

                                             <Grid item xs={2}>
                                                <Field
                                                   fullWidth
                                                   label={
                                                      currencyToShow ? (
                                                         <>
                                                            Cost (in {currencyToShow?.code}){' '}
                                                            <span style={{ color: 'red' }}>*</span>
                                                         </>
                                                      ) : (
                                                         <>
                                                            Cost{' '}
                                                            <span style={{ color: 'red' }}>*</span>
                                                         </>
                                                      )
                                                   }
                                                   name={`deliverable_milestones.${index}.cost`}
                                                   variant="outlined"
                                                   type="number"
                                                   value={val.cost}
                                                   onWheel={(e) => e.target.blur()}
                                                   onFocus={(e) => e.target.select()}
                                                   as={TextField}
                                                   error={
                                                      costInvalid ||
                                                      (touched.deliverable_milestones?.[index]?.cost &&
                                                         Boolean(errors.deliverable_milestones?.[index]?.cost))
                                                   }
                                                   // NEW: shows the message immediately when empty/0, regardless of touched
                                                   helperText={
                                                      costInvalid
                                                         ? 'Cost must be greater than 0'
                                                         : (touched.deliverable_milestones?.[index]?.cost &&
                                                            errors.deliverable_milestones?.[index]?.cost) ||
                                                         ''
                                                   }
                                                />
                                             </Grid>

                                             <Grid item xs={1}></Grid>
                                             <Grid item xs={2} align="right">
                                                {index === 0 ? (
                                                   <Button
                                                      type="button"
                                                      className="addFieldBtn"
                                                      onClick={() =>
                                                         arrayHelpers.push({
                                                            id:
                                                               values.deliverable_milestones.length +
                                                               1,
                                                            milestone: '',
                                                            cost: 0,
                                                         })
                                                      }
                                                   >
                                                      <AddIcon />
                                                   </Button>
                                                ) : (
                                                   <Button
                                                      type="button"
                                                      className="deleteFieldBtn"
                                                      onClick={() =>
                                                         handleRemoveItem(index, arrayHelpers)
                                                      }
                                                   >
                                                      <DeleteIcon />
                                                   </Button>
                                                )}
                                             </Grid>
                                          </Grid>
                                       </Grid>
                                    </Grid>
                                 )
                              })}
                              <Grid item xs={12} spacing={2} sx={{ marginTop: "40px" }}>
                                 <TextField
                                    fullWidth
                                    label={
                                       <>
                                          {`Total Milestone Cost (${currencyToShow?.code}) `}
                                          <span style={{ color: 'red' }}>*</span>
                                       </>
                                    }
                                    value={totalMilestoneCost.toFixed(2)}
                                    variant="outlined"
                                    InputProps={{ readOnly: true }}
                                 />
                              </Grid>
                              <Grid item xs={12} spacing={2}>
                                 <TextField
                                    fullWidth
                                    label={
                                       <>
                                          Total (Milestones + Commencement){' '}
                                          <span style={{ color: 'red' }}>*</span>
                                       </>
                                    }
                                    value={totalWithCommencement.toFixed(2)}
                                    variant="outlined"
                                    InputProps={{ readOnly: true }}
                                    sx={{ mt: 2 }}
                                 />
                              </Grid>
                           </>

                        )}
                     </FieldArray>

                     {/* Note */}
                     <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom align="left">
                           Note
                        </Typography>
                        <Field
                           fullWidth
                           label="Note"
                           name="note"
                           variant="outlined"
                           as={TextField}
                           multiline
                           rows={4}
                           value={values.note || ''}
                           onChange={(e) => setFieldValue('note', e.target.value)}
                           error={touched.note && Boolean(errors.note)}
                           helperText={touched.note && errors.note}
                           placeholder="Enter note here..."
                        />
                        {values.note.length > 0 && (
                           <Typography
                              variant="caption"
                              color="GrayText"
                              sx={{ mt: 0.5, display: 'block' }}
                           >
                              {values.note?.length} characters
                           </Typography>
                        )}

                     </Grid>
                  </Box>
               </Grid>

            </Grid>
         </Box>
      </div>
   );
}

export default StepTwoUpdate;